/**
 * PostHog product analytics on the platform: the same project as hey404.com,
 * and its cookie lives on .hey404.com, so a visitor from the landing is the
 * same person here. Loaded with PostHog's official stub (no npm dependency),
 * on app.hey404.com only, so local runs and previews never report. Signed-in
 * students are identified by their account id; inputs are masked in session
 * replays. The project key is public by design: it only lets browsers send
 * events.
 */
const posthogKey = "phc_xeAuh7JMeJssKuRumXMVzedWNoEYdFTUp5EDpMogpb7X";
const posthogHost = "https://us.i.posthog.com";
const enabled =
  typeof window !== "undefined" &&
  window.location.hostname === "app.hey404.com";

type Properties = Record<string, unknown>;
type PostHogStub = unknown[] & {
  __SV?: number;
  __loaded?: boolean;
  _i: unknown[];
  init: (key: string, options: Properties, name?: string) => void;
  capture: (event: string, properties?: Properties) => void;
  identify: (id: string, properties?: Properties) => void;
  reset: () => void;
  people: unknown[];
};

declare global {
  interface Window {
    posthog?: PostHogStub;
  }
}

/** PostHog's snippet, typed: queue calls until array.js takes over. */
function stub(): PostHogStub {
  const posthog = (window.posthog ?? []) as PostHogStub;
  if (posthog.__SV || posthog.__loaded) return posthog;
  window.posthog = posthog;
  posthog._i = [];
  posthog.init = (key, options, name) => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.crossOrigin = "anonymous";
    script.async = true;
    script.src = `${String(options.api_host).replace(".i.posthog.com", "-assets.i.posthog.com")}/static/array.js`;
    document.head.appendChild(script);
    const target = posthog;
    target.people = target.people ?? [];
    for (const method of [
      "capture",
      "identify",
      "reset",
      "register",
      "setPersonProperties",
      "get_distinct_id",
    ])
      (target as unknown as Record<string, unknown>)[method] = (
        ...args: unknown[]
      ) => target.push([method, ...args]);
    posthog._i.push([key, options, name]);
  };
  posthog.__SV = 1;
  return posthog;
}

let started = false;

/** Starts PostHog once (production only). Pageviews follow SPA navigation. */
export function initAnalytics() {
  if (!enabled || started) return;
  started = true;
  stub().init(posthogKey, {
    api_host: posthogHost,
    defaults: "2025-05-24",
    person_profiles: "identified_only",
  });
}

/** A product event; a no-op outside production. */
export function track(event: string, properties?: Properties) {
  if (!started) return;
  window.posthog?.capture(event, properties);
}

/** Ties this browser (and its landing visits) to the student's account. */
export function identifyStudent(user: {
  id: string;
  email: string;
  name?: string | null;
}) {
  if (!started) return;
  window.posthog?.identify(user.id, { email: user.email, name: user.name });
}

/** After sign-out, so the next person on this browser starts anonymous. */
export function resetAnalytics() {
  if (!started) return;
  window.posthog?.reset();
}
